import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';
import Shift from 'resources/Shift';

import { getCurrentLocationId } from 'selectors/session';

import shiftSortCompare from 'features/scheduleBuilder/ScheduleBuilderView/utils/shiftSortCompare';

import { dateFromTime, duration, momentDate } from 'util/dateTime';

import { selectDatesInCurrentRange } from './index';

/**
 * Because we get shifts that belong to a location, this is implied that
 * we have an open shift
 */
export const selectOpenShifts = createSelector(
  state =>
    getCurrentLocationId(state) || state.get('session')?.currentLocation?.id,
  state => Shift.selectAllEntities(state),
  (currentLocationId, shifts) =>
    shifts.filter(shift => {
      const shiftLocationId =
        shift?.relationships?.owner?.data?.type === 'location'
          ? shift?.relationships?.owner?.data?.id
          : undefined;

      return String(shiftLocationId) === String(currentLocationId);
    })
);

export const selectOpenShiftsCountOnDateRange = createSelector(
  selectOpenShifts,
  state => selectDatesInCurrentRange(state),
  (openShifts, datesInCurrentRange) => {
    const openShiftsInRange = openShifts.filter(openShift =>
      datesInCurrentRange.includes(dateFromTime(openShift.attributes.startAt))
    );
    return openShiftsInRange.length;
  }
);

export const selectFormattedOpenShiftsHoursTotal = createSelector(
  selectOpenShifts,
  shifts => {
    const totalHours = shifts.reduce((total, shift) => {
      const dateFormat = 'YYYY-MM-DD HH:mm';
      const startAt = momentDate(shift.attributes.startAt, dateFormat);
      const endAt = momentDate(shift.attributes.endAt, dateFormat);

      const diff = duration(endAt.diff(startAt)).asHours();
      return (total += diff);
    }, 0);

    return (Math.round(totalHours * 100) / 100).toFixed(2);
  }
);

export const selectOpenShiftsOnDate = createCachedSelector(
  selectOpenShifts,
  (_, props) => props.date,
  (openShifts, date) =>
    openShifts.filter(s => dateFromTime(s.attributes.startAt) === date)
)((_, { date }) => `selectOpenShiftsOnDate-${date}`);

export const selectOpenShiftsSortedOnDate = createSelector(
  selectOpenShiftsOnDate,
  openShifts => openShifts.sort(shiftSortCompare)
);
